import { cn } from '@trafiflow/shared-ui'
import { Building2, Target, Users, Package, TrendingUp, Check, Edit3 } from 'lucide-react'

interface BrandProfile {
  name: string
  description?: string
  category?: string
  product_type?: string
  value_proposition?: string
  target_audience?: string
  logo_url?: string
}

interface AdPatterns {
  hook_patterns?: string[]
  cta_patterns?: string[]
  proof_patterns?: string[]
  top_hook_style?: string
  top_cta?: string
}

interface AdLibrarySummary {
  total_ads: number
  video_count: number
  image_count: number
  sample_thumbnails?: string[]
}

interface BrandIntelCardProps {
  brand: BrandProfile
  patterns?: AdPatterns
  adLibrary?: AdLibrarySummary
  onEdit?: () => void
  onConfirm?: () => void
  isConfirmed?: boolean
  className?: string
}

export default function BrandIntelCard({
  brand,
  patterns,
  adLibrary,
  onEdit,
  onConfirm,
  isConfirmed,
  className,
}: BrandIntelCardProps) {
  return (
    <div className={cn(
      'rounded-xl border bg-tf-bg-card overflow-hidden',
      isConfirmed ? 'border-emerald-500/30' : 'border-blue-500/30',
      className,
    )}>
      {/* Header */}
      <div className={cn(
        'px-5 py-4 border-b',
        isConfirmed ? 'bg-emerald-500/5 border-emerald-500/20' : 'bg-blue-500/5 border-blue-500/20',
      )}>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            {brand.logo_url ? (
              <img
                src={brand.logo_url}
                alt={brand.name}
                className="w-10 h-10 rounded-lg object-cover bg-white"
              />
            ) : (
              <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center">
                <Building2 className="w-5 h-5 text-white" />
              </div>
            )}
            <div>
              <h3 className="font-bold text-tf-text flex items-center gap-2">
                {brand.name}
                {isConfirmed && <Check className="w-4 h-4 text-emerald-500" />}
              </h3>
              {brand.category && (
                <p className="text-xs text-tf-text-muted">{brand.category}</p>
              )}
            </div>
          </div>
          {onEdit && (
            <button
              onClick={onEdit}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium text-tf-text-muted hover:text-tf-text hover:bg-tf-bg-muted transition-colors"
            >
              <Edit3 className="w-3.5 h-3.5" />
              Edit
            </button>
          )}
        </div>
      </div>

      {/* Content */}
      <div className="p-5 space-y-4">
        {/* Brand understanding */}
        {brand.description && (
          <div className="space-y-1">
            <p className="text-sm text-tf-text">{brand.description}</p>
          </div>
        )}

        {/* Key attributes */}
        <div className="grid grid-cols-2 gap-3">
          {brand.product_type && (
            <div className="flex items-start gap-2">
              <Package className="w-4 h-4 text-blue-500 mt-0.5" />
              <div>
                <p className="text-[10px] text-tf-text-muted uppercase tracking-wider">Product</p>
                <p className="text-xs text-tf-text">{brand.product_type}</p>
              </div>
            </div>
          )}
          {brand.target_audience && (
            <div className="flex items-start gap-2">
              <Users className="w-4 h-4 text-purple-500 mt-0.5" />
              <div>
                <p className="text-[10px] text-tf-text-muted uppercase tracking-wider">Audience</p>
                <p className="text-xs text-tf-text">{brand.target_audience}</p>
              </div>
            </div>
          )}
          {brand.value_proposition && (
            <div className="flex items-start gap-2 col-span-2">
              <Target className="w-4 h-4 text-emerald-500 mt-0.5" />
              <div>
                <p className="text-[10px] text-tf-text-muted uppercase tracking-wider">Value Prop</p>
                <p className="text-xs text-tf-text">{brand.value_proposition}</p>
              </div>
            </div>
          )}
        </div>

        {/* Ad Library Summary */}
        {adLibrary && adLibrary.total_ads > 0 && (
          <div className="pt-3 border-t border-tf-border">
            <div className="flex items-center justify-between mb-2">
              <p className="text-xs font-medium text-tf-text-muted uppercase tracking-wider">
                Your Ad Library
              </p>
              <span className="text-xs text-tf-text">
                {adLibrary.total_ads} ads found
              </span>
            </div>
            <div className="flex gap-4 text-xs text-tf-text-muted">
              <span>{adLibrary.video_count} videos</span>
              <span>{adLibrary.image_count} images</span>
            </div>
            {/* Thumbnail preview */}
            {adLibrary.sample_thumbnails && adLibrary.sample_thumbnails.length > 0 && (
              <div className="flex gap-1.5 mt-2">
                {adLibrary.sample_thumbnails.slice(0, 4).map((thumb, i) => (
                  <div key={i} className="w-12 h-12 rounded-md bg-tf-bg-muted overflow-hidden">
                    <img src={thumb} alt="" className="w-full h-full object-cover" />
                  </div>
                ))}
                {adLibrary.total_ads > 4 && (
                  <div className="w-12 h-12 rounded-md bg-tf-bg-muted flex items-center justify-center">
                    <span className="text-[10px] text-tf-text-muted">+{adLibrary.total_ads - 4}</span>
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {/* Detected Patterns */}
        {patterns && (patterns.hook_patterns?.length || patterns.cta_patterns?.length) && (
          <div className="pt-3 border-t border-tf-border">
            <div className="flex items-center gap-2 mb-2">
              <TrendingUp className="w-4 h-4 text-amber-500" />
              <p className="text-xs font-medium text-tf-text-muted uppercase tracking-wider">
                Detected Patterns
              </p>
            </div>
            <div className="space-y-2">
              {patterns.hook_patterns && patterns.hook_patterns.length > 0 && (
                <div>
                  <span className="text-[10px] text-tf-text-muted">Hooks: </span>
                  <div className="inline-flex flex-wrap gap-1 mt-1">
                    {patterns.hook_patterns.slice(0, 3).map((hook, i) => (
                      <span key={i} className="px-2 py-0.5 rounded-full bg-blue-500/10 text-blue-500 text-[10px]">
                        {hook}
                      </span>
                    ))}
                  </div>
                </div>
              )}
              {patterns.cta_patterns && patterns.cta_patterns.length > 0 && (
                <div>
                  <span className="text-[10px] text-tf-text-muted">CTAs: </span>
                  <div className="inline-flex flex-wrap gap-1 mt-1">
                    {patterns.cta_patterns.slice(0, 3).map((cta, i) => (
                      <span key={i} className="px-2 py-0.5 rounded-full bg-emerald-500/10 text-emerald-500 text-[10px]">
                        {cta}
                      </span>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Actions */}
      {onConfirm && !isConfirmed && (
        <div className="px-5 py-3 bg-tf-bg-muted border-t border-tf-border flex justify-end gap-2">
          {onEdit && (
            <button
              onClick={onEdit}
              className="px-4 py-2 rounded-lg text-sm font-medium text-tf-text hover:bg-tf-bg-hover transition-colors"
            >
              Edit Brand Info
            </button>
          )}
          <button
            onClick={onConfirm}
            className="flex items-center gap-2 px-4 py-2 rounded-lg bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium transition-colors"
          >
            <Check className="w-4 h-4" />
            Looks Good
          </button>
        </div>
      )}
    </div>
  )
}

// Helper to parse brand from discovery artifact
export function parseBrandFromArtifact(artifact: Record<string, unknown> | undefined): BrandProfile | null {
  if (!artifact) return null

  // Handle nested brand_profile object if present
  const brandProfile = artifact.brand_profile as Record<string, unknown> | undefined

  // Try multiple field name variations
  const name =
    (artifact.brand_name as string) ||
    (artifact.name as string) ||
    (brandProfile?.name as string) ||
    (brandProfile?.brand_name as string) ||
    (artifact.business_name as string) ||
    null

  // If no name found, return null to avoid showing "Unknown Brand"
  if (!name) return null

  return {
    name,
    description:
      (artifact.description as string) ||
      (brandProfile?.description as string) ||
      (artifact.brand_description as string),
    category:
      (artifact.category as string) ||
      (brandProfile?.category as string) ||
      (artifact.industry as string),
    product_type:
      (artifact.product_type as string) ||
      (brandProfile?.product_type as string) ||
      (artifact.product_category as string),
    value_proposition:
      (artifact.value_proposition as string) ||
      (brandProfile?.value_proposition as string) ||
      (artifact.value_prop as string),
    target_audience:
      (artifact.target_audience as string) ||
      (brandProfile?.target_audience as string) ||
      (artifact.audience as string),
    logo_url:
      (artifact.logo_url as string) ||
      (brandProfile?.logo_url as string) ||
      (artifact.og_image as string),
  }
}

// Helper to parse patterns from competitor intel artifact
export function parsePatternsFromArtifact(artifact: Record<string, unknown> | undefined): AdPatterns | null {
  if (!artifact) return null

  return {
    hook_patterns: artifact.hook_patterns as string[] | undefined,
    cta_patterns: artifact.cta_patterns as string[] | undefined,
    proof_patterns: artifact.proof_patterns as string[] | undefined,
    top_hook_style: artifact.top_hook_style as string | undefined,
    top_cta: artifact.top_cta as string | undefined,
  }
}

// Helper to parse ad library from brand ads artifact
export function parseAdLibraryFromArtifact(artifact: Record<string, unknown> | undefined): AdLibrarySummary | null {
  if (!artifact) return null

  return {
    total_ads: (artifact.ad_count as number) || 0,
    video_count: (artifact.video_count as number) || 0,
    image_count: ((artifact.ad_count as number) || 0) - ((artifact.video_count as number) || 0),
    sample_thumbnails: artifact.sample_thumbnails as string[] | undefined,
  }
}
